import * as Sentry from '@sentry/react';

import { HID_POS_USAGE_PAGE, LOG_PREFIX, NEWLAND_VENDOR_ID } from 'models/QRCodeScannerDevice/constants';
import { ScannerFailure, ScannerFailureReport } from 'models/QRCodeScannerDevice/types';
import { serializeDevices } from 'models/QRCodeScannerDevice/utils';

/**
 * Builds the Sentry event sent when the scanner cannot be connected.
 * Nested data (HID collections, dealers) is stringified: Sentry `normalizeDepth` would truncate it.
 */
export const buildScannerFailureReport = ({ reason, devices, attempts, kioskContext, error }: ScannerFailure): ScannerFailureReport => {
  const sentryError = new Error(`QR code scanner not connected: ${reason}`);
  sentryError.name = 'QrCodeScannerConnectionError';

  return {
    sentryError,
    captureContext: {
      tags: { qr_scanner_failure_reason: reason },
      contexts: {
        kiosk: kioskContext && { ...kioskContext, dealers: JSON.stringify(kioskContext.dealers, null, 2) },
        qrCodeScanner: {
          reason,
          attempts,
          expectedVendorId: NEWLAND_VENDOR_ID,
          expectedUsagePage: HID_POS_USAGE_PAGE,
          error: error ? String(error) : undefined,
          hidDevices: serializeDevices(devices),
          userAgent: navigator.userAgent,
          url: window.location.href,
        },
      },
    },
  };
};

// A failing Sentry call is only logged
export const reportScannerFailureToSentry = (failure: ScannerFailure): void => {
  try {
    console.log(`${LOG_PREFIX} Reporting connection failure to Sentry: ${failure.reason}`, failure.kioskContext);
    const { sentryError, captureContext } = buildScannerFailureReport(failure);
    Sentry.captureException(sentryError, captureContext);
  } catch (error) {
    console.log(`${LOG_PREFIX} Sentry report failed`, error);
  }
};
